import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ProductItemTypes } from '../_types/productItem.types'
import { useIsWhiteLabel } from '@/components/app/providers/whiteLabelProvider/WhiteLabelProvider'

export const suggestionsVillages = [
  'فیلبند',
  'رینه',
  'لفور',
  'درگهان',
  'شیرگاه',
  'سوباتان',
  'اسالم',
  'چمستان',
  'سرولات',
  'برغان',
  'دیلمان',
  'ابیانه',
  'میگون',
  'کندلوس',
]
const ProductMeta: React.FC<{
  productState: ProductItemTypes
  url: string
  host: string
}> = ({ productState, url, host }) => {
  const router = useRouter()
  const isWhiteLabel = useIsWhiteLabel()

  let typeName = 'ویلا و سوئیت'

  const city = productState?.location?.city || ''
  const province = productState?.location?.province || ''

  switch (productState?.type) {
    case 'villa':
      typeName = 'ویلا و سوئیت'
      break

    case 'apartment':
      typeName = 'خانه و آپارتمان'
      break

    case 'boomgardi':
      typeName = 'اقامتگاه بومگردی'
      break

    case 'cottage':
      typeName = 'کلبه'
      break

    default:
      typeName = 'اقامتگاه'
      break
  }

  const parentId = productState?.parent?.id

  let position4 = `{"@type":"ListItem","position":4,"name":"${productState?.title?.replace(
    /"/g,
    '\\"',
  )}","item":"https://www.shab.ir/houses/show/${productState?.id}"}`

  if (parentId) {
    position4 = `
      {"@type":"ListItem","position":4,"name":"${productState?.parent?.title?.replace(
        /"/g,
        '\\"',
      )}","item":"https://www.shab.ir/search?parent_id=${parentId}"},
      {"@type":"ListItem","position":5,"name":"${productState?.title?.replace(
        /"/g,
        '\\"',
      )}","item":"https://www.shab.ir/houses/show/${productState?.id}"}
    `
  }

  if (
    suggestionsVillages.some(item =>
      productState?.location?.village.includes(item),
    )
  ) {
    position4 = `
     {"@type":"ListItem","position":4,"name":"${`اجاره ویلا در ${productState?.location?.village.replace(
       /"/g,
       '\\"',
     )}`}","item":"https://www.shab.ir/search/village/${productState?.location?.village.replace(
       /"/g,
       '\\"',
     )}"},
    {"@type":"ListItem","position":5,"name":"${`اجاره ویلا در ${productState?.title?.replace(
      /"/g,
      '\\"',
    )}`}","item":"https://www.shab.ir/houses/show/${productState?.id}"}
    `

    if (parentId) {
      position4 = `
      {"@type":"ListItem","position":4,"name":"${`اجاره ویلا در ${productState?.location?.village.replace(
        /"/g,
        '\\"',
      )}`}","item":"https://www.shab.ir/search/village/${productState?.location?.village.replace(
        /"/g,
        '\\"',
      )}"},
      {"@type":"ListItem","position":5,"name":"${`${productState.parent?.title.replace(
        /"/g,
        '\\"',
      )}`}","item":"https://www.shab.ir/houses/show/${parentId}"},
    {"@type":"ListItem","position":6,"name":"${`${productState?.title?.replace(
      /"/g,
      '\\"',
    )}`}","item":"https://www.shab.ir/houses/show/${productState?.id}"}
      `
    }
  }

  const prodImage = productState?.pictures?.records
    ? productState?.pictures?.records[0]?.path
    : null

  const headTitle = `${productState?.title}${router.query.mid_term ? ' (اجاره بلندمدت)' : ''} – ${typeName || ''} در ${city || ''} ${!isWhiteLabel ? '| شب' : ''}`

  const description = productState?.about || headTitle
  const image = prodImage || `https://www.shab.ir/images/favicon.ico`
  const href = isWhiteLabel
    ? `${host}${url?.split('?')?.[0]}`
    : `https://www.shab.ir${url?.split('?')[0]}`
  const hrefObject = new URL(href)
  const canonHref = hrefObject.origin + (hrefObject.pathname || '')

  function getDateAfterTwoMonths() {
    const currentDate = new Date()
    currentDate.setMonth(currentDate.getMonth() + 2)

    // Ensure correct format yyyy-mm-dd
    const year = currentDate.getFullYear()
    const month = String(currentDate.getMonth() + 1).padStart(2, '0') // Months are zero-based
    const day = String(currentDate.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  const normalizedHouseName = productState?.title?.replaceAll('"', "'")
  const normalizedHouseDescription = productState?.about?.replaceAll('"', "'")

  return (
    <Head>
      <title>{headTitle}</title>

      {!isWhiteLabel && (
        <script
          defer={true}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
            {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[
            {"@type":"ListItem","position":1,"name":"اجاره ویلا و سوئیت","item":"https://www.shab.ir"},
            {"@type":"ListItem","position":2,"name":"اجاره ویلا در استان ${productState?.location?.province}","item":"https://www.shab.ir/search/province/${productState?.location?.province}"},
            {"@type":"ListItem","position":3,"name":"اجاره ویلا در ${productState?.location?.city}","item":"https://www.shab.ir/search/city/${productState?.location?.city}"},
            ${position4}]}
          `,
          }}
        ></script>
      )}
      {productState?.bookable_status !== 'non_bookable' &&
        productState.location?.latitude &&
        productState.location?.longitude &&
        productState.title &&
        !isWhiteLabel && (
          <script
            defer={true}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
              {
                "@context": "https://schema.org",
                "@type": "Place",
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": "${productState.location?.latitude}",
                  "longitude": "${productState.location?.longitude}"
                },
                "name": "${normalizedHouseName}"
              }
            `,
            }}
          />
        )}
      {productState?.bookable_status !== 'non_bookable' &&
        Number(productState?.rates?.rank) &&
        !isWhiteLabel && (
          <script
            defer={true}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
          {
            "@context": "https://schema.org/", 
            "@type": "Product", 
            "name": "${normalizedHouseName}",
            "image": "${image}",
            "description": ${JSON.stringify(description)},
            "offers": {
              "@type": "Offer",
              "url": "${href}",
              "priceCurrency": "IRR",
              "availability": "https://schema.org/InStock",
              "price": "${productState?.pricing?.price_to_show?.amount}",
              "priceValidUntil": "${getDateAfterTwoMonths()}",
              "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "applicableCountry": "IR",
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 7,
                "returnMethod": "https://schema.org/ReturnByMail",
                "returnFees": "https://schema.org/FreeReturn"
              },
              "shippingDetails":{
                "@type": "OfferShippingDetails",
                "shippingRate": {
                  "@type": "MonetaryAmount",
                  "value": "0.00",
                  "currency": "IRR"
                },
                "shippingDestination": {
                  "@type": "DefinedRegion",
                  "addressCountry": "IR"
                },
                "deliveryTime": {
                    "@type": "ShippingDeliveryTime",
                    "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 1,
                        "maxValue": 2,
                        "unitCode": "DAY"
                    },
                    "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 2,
                        "maxValue": 5,
                        "unitCode": "DAY"
                    }
                }
              }
            },
            ${
              Number(productState?.reviews_count)
                ? `"aggregateRating": {
                      "@type": "AggregateRating",
                      "ratingValue": "${Number(productState?.rates?.rank)}",
                      "bestRating": "5",
                      "worstRating": "1",
                      "ratingCount": "${Number(productState?.reviews_count)}",
                      "reviewCount": "${Number(productState?.reviews_count)}"
                    },`
                : ''
            }
            "review": [
              ${productState?.reviews
                ?.map(review =>
                  Number(review?.point_average) && !!review?.user?.name?.trim()
                    ? `{
                    "@type": "Review",
                    "name": "Value purchase",
                    "reviewBody": ${JSON.stringify(review?.comment)},
                    "reviewRating": {
                      "@type": "Rating",
                      "ratingValue": "${review?.point_average >= 1 ? review?.point_average : 1}",
                      "bestRating": "5",
                      "worstRating": "1"
                    },
                    "datePublished": "${review?.checkout_date?.slice(0, 10)}",
                    "author": {"@type": "Person", "name": "${review?.user?.name}"}
                  }`
                    : '',
                )
                ?.filter(i => !!i)
                .join(',')}
            ]
          }`,
            }}
          ></script>
        )}

      {productState?.bookable_status !== 'non_bookable' && !isWhiteLabel && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `
        {
          "@context": "https://schema.org",
          "@type": "Accommodation",
          "name": "${normalizedHouseName}",
          "description": "${normalizedHouseDescription}",
          "accommodationCategory": ["${typeName}", "${productState?.type}"],
          "numberOfBathroomsTotal": ${productState?.features?.filled_categories?.spaces?.find(item => item.feature_name === 'bathroom')?.meta_data?.count || 0},
          "numberOfRooms": ${productState?.spaces?.master_rooms + productState?.spaces?.normal_rooms},
          "floorSize": {
            "@type": "QuantitativeValue",
            "value": ${productState?.building_area},
            "unitCode": "MTK"
          },
          "petsAllowed": ${!!productState?.rules?.records?.pets_allowed},
          "smokingAllowed": ${!!productState?.rules?.records?.smoking},
          "url": "https://www.shab.ir/houses/show/${productState?.id}",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "${city}",
            "addressRegion": "${province}",
            "addressCountry": "IR"
          }
        }
        `,
          }}
        ></script>
      )}

      <meta name="description" content={description} />
      <meta property="og:title" content={headTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={href} />
      <meta property="og:site_name" content={isWhiteLabel ? '' : 'سایت شب'} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@shabhost" />
      <meta name="twitter:title" content={headTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image:src" content={image} />

      {!isWhiteLabel && (
        <link
          rel="canonical"
          href={
            parentId
              ? `https://www.shab.ir/search?parent_id=${parentId}`
              : canonHref
          }
        />
      )}

      <style>
        {`
          body {
            background-color: #fcfcfc !important;
          }
        `}
      </style>
    </Head>
  )
}

export default ProductMeta
